import React, { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Button } from '@/shared/ui';
import styles from './ScenarioSelect.module.css';

interface ScenarioSelectProps {
    question: string;
    options: string[];
    correctIndex: number;
    onComplete?: () => void;
}

const ScenarioSelect: React.FC<ScenarioSelectProps> = ({
    question,
    options,
    correctIndex,
    onComplete
}) => {
    const [selectedIndex, setSelectedIndex] = useState<number | null>(null);
    const [isCorrect, setIsCorrect] = useState<boolean | null>(null);
    const [showFeedback, setShowFeedback] = useState(false);

    const handleOptionSelect = (index: number) => {
        if (showFeedback) return;

        setSelectedIndex(index);
        const correct = index === correctIndex;
        setIsCorrect(correct);
        setShowFeedback(true);

        if (correct && onComplete) {
            setTimeout(onComplete, 1500);
        }
    };

    return (
        <div className={styles.container}>
            <div className={styles.questionCard}>
                <h3 className={styles.questionText}>{question}</h3>

                <div className={styles.optionsGrid}>
                    {options.map((option, index) => (
                        <Button
                            key={index}
                            variant={selectedIndex === index ? (isCorrect ? 'primary' : 'danger') : 'outline'}
                            className={`${styles.optionButton} ${selectedIndex === index ? styles.selected : ''}`}
                            onClick={() => handleOptionSelect(index)}
                            disabled={showFeedback}
                            aria-label={`${String.fromCharCode(65 + index)}: ${option}`}
                        >
                            <span className={styles.optionLetter}>
                                {String.fromCharCode(65 + index)}
                            </span>
                            <span className={styles.optionText}>{option}</span>
                        </Button>
                    ))}
                </div>

                <AnimatePresence>
                    {showFeedback && (
                        <motion.div
                            initial={{ opacity: 0, y: 10 }}
                            animate={{ opacity: 1, y: 0 }}
                            exit={{ opacity: 0, y: -10 }}
                            className={`${styles.feedback} ${isCorrect ? styles.correct : styles.incorrect}`}
                        >
                            {isCorrect ? (
                                <div className={styles.feedbackContent}>
                                    <span className={styles.feedbackIcon}>✅</span>
                                    <div>
                                        <strong>Correct!</strong>
                                        <p>Great job identifying the right approach for this scenario.</p>
                                    </div>
                                </div>
                            ) : (
                                <div className={styles.feedbackContent}>
                                    <span className={styles.feedbackIcon}>❌</span>
                                    <div>
                                        <strong>Not quite.</strong>
                                        <p>Review the lesson content above and try again. The correct answer is highlighted.</p>
                                        <Button
                                            variant="ghost"
                                            size="small"
                                            onClick={() => setShowFeedback(false)}
                                            className={styles.retryButton}
                                        >
                                            Try Again
                                        </Button>
                                    </div>
                                </div>
                            )}
                        </motion.div>
                    )}
                </AnimatePresence>
            </div>
        </div>
    );
};

export default ScenarioSelect;
